export const WORKFLOW_STEP_TYPE_IDS = {
  signing: 'signing',
  notify: 'notify',
  doclogixApproval: 'doclogix-approval',
  doclogixUpdate: 'doclogix-update',
} as const;

export type WorkflowStepTypeId =
  (typeof WORKFLOW_STEP_TYPE_IDS)[keyof typeof WORKFLOW_STEP_TYPE_IDS];

export const WORKFLOW_ENGINE_STEP_TYPE_IDS = {
  fork: 'fork',
  join: 'join',
  end: 'end',
} as const;

export interface WorkflowStepTypeDto {
  stepTypeId: string;
}

export type WorkflowStepKind =
  | 'signatureQualified'
  | 'signatureUnqualified'
  | 'acknowledgement'
  | 'endorsement'
  | 'notification'
  | 'doclogixApproval'
  | 'doclogixUpdate';

export type StepAction = 'qualified' | 'unqualified' | 'acknowledge' | 'endorse';

export interface SigningStepConfig {
  action: StepAction;
}

export interface NotifyStepConfig {
  recipients: string[];
  message: string;
}

export type DoclogixApprovalRecipients = 'none' | 'ownerManager';

export interface DoclogixApprovalStepConfig {
  doclogixRecipients: DoclogixApprovalRecipients;
}

export type DoclogixFieldValue = string | number | boolean;

export interface DoclogixUpdateStepField {
  name: string;
  value: DoclogixFieldValue;
}

export interface DoclogixUpdateStepConfig {
  fields: DoclogixUpdateStepField[];
}

export type WorkflowStepConfig = Partial<
  SigningStepConfig & NotifyStepConfig & DoclogixApprovalStepConfig & DoclogixUpdateStepConfig
>;

export interface WorkflowTransitionDto {
  outcome: string | null;
  target: WorkflowTransitionTarget;
  targetStepOrder: number | null;
}

export interface WorkflowStepDto {
  id: number;
  order: number;
  stepType: string;
  stepTypeId: string;
  config: string | null;
  assigneeEmail: string | null;
  isAssigneeOverridable: boolean;
  endMode: WorkflowEndMode | null;
  joinStrategy: WorkflowJoinStrategy | null;
  forkStepOrder: number | null;
  branchFirstStepOrder: number | null;
  transitions: WorkflowTransitionDto[];
}

export interface WorkflowDefinitionDto {
  definitionNumber: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
  inProgressInstanceCount: number;
  steps: WorkflowStepDto[];
}

export interface WorkflowDefinitionSummaryDto {
  definitionNumber: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
  stepTypes: string[];
}

export interface WorkflowDefinitionVersionDto {
  version: number;
  name: string;
  savedAt: string;
  steps: WorkflowStepDto[];
}

export type WorkflowSubjectType = 'document' | 'form-template' | 'folder';

export type WorkflowStartBlockerReason =
  | 'integrationDisabled'
  | 'workflowAlreadyRunning'
  | 'documentNotFound'
  | 'documentCompleted'
  | 'documentRejected'
  | 'documentVoided'
  | 'documentAlreadySigned'
  | 'documentRejectedBySigner'
  | 'documentOutForSigning';

export interface WorkflowSubjectStateDto {
  isBound: boolean;
  workflowDefinitionNumber: number | null;

  boundAt: WorkflowSubjectType | null;
  boundAtName: string | null;
  isInherited: boolean;

  isRunning: boolean;
  workflowInstanceId: number | null;
  canStart: boolean;
  startBlockerReason: WorkflowStartBlockerReason | null;
}

export interface SaveWorkflowStepDto {
  order: number;
  stepType: string;
  stepTypeId: string;
  config: string | null;
  assigneeEmail: string;
  isAssigneeOverridable: boolean;
  endMode?: WorkflowEndMode;
  joinStrategy?: WorkflowJoinStrategy;
  transitions: WorkflowTransitionDto[];
}

export type WorkflowStepAssigneeOverrides = Record<number, string>;

export interface CreateWorkflowDefinitionDto {
  name: string;
  description: string;
  steps: SaveWorkflowStepDto[];
}

export interface UpdateWorkflowDefinitionDto extends CreateWorkflowDefinitionDto {
  isActive: boolean;
  expectedVersion: number;
}

export enum WorkflowTransitionTarget {
  Step = 0,
  PreviousStep = 1,
  Start = 2,
}

export enum WorkflowEndMode {
  EndWorkflow = 0,
  EndBranch = 1,
}

export enum WorkflowJoinStrategy {
  All = 0,
  Any = 1,
}

export enum WorkflowInstanceStatus {
  InProgress = 1,
  Completed = 2,
  Rejected = 3,
  Cancelled = 4,
}

export enum WorkflowStepInstanceStatus {
  Active = 2,
  Completed = 3,
  Rejected = 4,
  Cancelled = 6,
}

export interface WorkflowStepInstanceDto {
  id: number;
  stepId: number;
  previousStepInstanceId: number | null;
  previousForkExecutionId: number | null;
  forkBranchId: number | null;
  activatedAt: string | null;
  stepOrder: number;
  stepType: string;
  stepTypeId: string;
  assigneeEmail: string;
  status: WorkflowStepInstanceStatus;
  outcome: string | null;
  completedAt: string | null;
  completedByEmail: string | null;
  comment: string | null;
}

export interface WorkflowInstanceDto {
  id: number;
  definitionNumber: number;
  definitionName: string;
  definitionVersion: number;
  correlationType: string;
  correlationId: string;
  status: WorkflowInstanceStatus;
  activeStepInstanceIds: number[];
  createdAt: string;
  updatedAt: string;
  stepInstances: WorkflowStepInstanceDto[];
}

export interface WorkflowStateDto {
  instance: WorkflowInstanceDto;
  activeSteps: unknown[];
  activatedSteps: unknown[];
  cancelledStepInstanceIds: number[];
}

export interface WorkflowInstanceStepSummaryDto {
  stepInstanceId: number;
  order: number;
  stepType: string;
  assigneeEmail: string | null;
  status: WorkflowStepInstanceStatus;
}

export interface WorkflowDashboardStepDto {
  stepInstanceId: number;
  stepOrder: number;
  stepType: string;
  assigneeEmail: string;
  status: WorkflowStepInstanceStatus;
}

export interface WorkflowDashboardRunDto {
  documentId: number;
  documentName: string;
  workflowInstanceId: number;
  workflowName: string;
  status: WorkflowInstanceStatus;
  updatedAt: string;
  totalSteps: number;
  completedSteps: number;
  steps: WorkflowDashboardStepDto[];
}

export interface WorkflowMonitoringInstanceDto {
  instanceId: number;
  definitionNumber: number;
  definitionName: string;
  definitionVersion: number;
  correlationId: string;
  documentId: number | null;
  documentName: string | null;
  status: WorkflowInstanceStatus;
  activeSteps: WorkflowInstanceStepSummaryDto[];
  totalSteps: number;
  completedSteps: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowMonitoringPageDto {
  items: WorkflowMonitoringInstanceDto[];
  hasMore: boolean;
}

export interface WorkflowInstanceFilterDto {
  status?: WorkflowInstanceStatus;
  definitionNumber?: number;
  assigneeEmail?: string;
  fromDate?: string;
  toDate?: string;
  beforeCreatedAt?: string;
  beforeId?: number;
  limit?: number;
}
